import { StatementFormData } from 'src/Libs/installation-statement/types/general'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCallback, useEffect, useState } from 'react'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import {
  confirmInstallationStatement,
  getCompanyAddressInfo,
  getCompanyInfo,
  getInstallationStatement,
  saveInstallationStatement,
} from 'src/Libs/installation-statement/api/installation-statement.api'
import { fromStatementDtoToModelTransformer } from 'src/Libs/installation-statement/transformers/from-statement-dto-to-model.transformer'
import useImages from 'src/Libs/installation-statement/hooks/use-images'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationStatementApiDto, InstallationStatementModel } from 'src/Libs/installation-statement/models/statement'
import { fromStatementModelToDtoTransformer } from 'src/Libs/installation-statement/transformers/from-statement-model-to-dto.transformer'
import { isBiDiWallboxConnected } from 'src/Libs/installation-statement/api/wallbox.api'
import { ApiResult } from 'src/Libs/api'
import { DeviceInfoForStatement } from 'src/Libs/installation-statement/components/Modals/InstallationStatementDialog'
import { StatusCodes } from 'src/Libs/http-status-codes'

interface UseFetchDataHook {
  error?: Error | AuthFetchError
  isLoading: boolean
  saveStatement: (deviceLinkId: number, installationId: number, statement: InstallationStatementApiDto) => Promise<number | false>
  confirmStatement: (
    deviceLinkId: number,
    installationId: number,
    statement: InstallationStatementApiDto
  ) => Promise<ApiResult<InstallationStatementModel, Error | AuthFetchError>>
}

/**
 * This hook fetches installation statement data for a given installation and returns either the
 * fetched data or a default data in case no data can/should be fetched
 *
 * Also provides functions for saving an incomplete statement, and confirming a completed statement
 *
 * @param installation - the installation, the statement will be fetched for
 * @param callback - function to call, when data is arrived
 */
const useFetchData = (
  installation: DeviceInfoForStatement,
  defaultValues: StatementFormData,
  callback: (params: { statementFormData: StatementFormData; hasError: false } | { hasError: true }) => void
): UseFetchDataHook => {
  const authFetch = useAuthFetchWithResponse()
  const { getAllUploadedImages } = useImages()
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const [isLoading, setIsLoading] = useState(true)

  const getStatementFormData = useCallback(
    async (installation: DeviceInfoForStatement) => {
      const [statementResponse, imagesResponse, wallboxConnectedResponse, companyAddressResponse, companyInfoResponse] = await Promise.allSettled([
        getInstallationStatement(installation, authFetch),
        getAllUploadedImages(installation.deviceLinkId, installation.id),
        isBiDiWallboxConnected(installation.deviceId, authFetch),
        installation.installerCompanyId && getCompanyAddressInfo(installation.installerCompanyId, authFetch),
        installation.installerCompanyId && getCompanyInfo(installation.installerCompanyId, authFetch),
      ])

      const isStatementResponseForbidden =
        statementResponse.status === 'fulfilled' && (statementResponse.value.error as AuthFetchError)?.response?.status === StatusCodes.FORBIDDEN

      if (isStatementResponseForbidden) {
        enqueueErrorStack(t('errors.installation-statement.forbidden'))
      } else {
        if (statementResponse.status !== 'fulfilled' || statementResponse.value.hasErrors) {
          enqueueErrorStack(t('errors.installation-statement.loadError'))
        }
        if (imagesResponse.status !== 'fulfilled' || !imagesResponse.value) {
          enqueueErrorStack(t('errors.installation-statement.imageLoadError'))
        }
      }

      if (wallboxConnectedResponse.status !== 'fulfilled' || wallboxConnectedResponse.value.hasErrors) {
        enqueueErrorStack(t('errors.installation-statement.loadError'))
      }

      const companyAddress =
        (companyAddressResponse.status === 'fulfilled' && companyAddressResponse.value && companyAddressResponse.value.value) || null

      const companyInfo = (companyInfoResponse.status === 'fulfilled' && companyInfoResponse.value && companyInfoResponse.value.value) || null

      if (
        statementResponse.status === 'fulfilled' &&
        statementResponse.value.value &&
        imagesResponse.status === 'fulfilled' &&
        imagesResponse.value?.images &&
        wallboxConnectedResponse.status === 'fulfilled' &&
        wallboxConnectedResponse.value
      ) {
        const formData = fromStatementDtoToModelTransformer({
          data: statementResponse.value.value,
          imagesData: imagesResponse.value,
          isWallboxConnected: !!wallboxConnectedResponse.value.value,
          companyData: companyAddress,
          companyInfo,
        })
        callback({ statementFormData: formData, hasError: false })
      } else {
        callback({ hasError: true })
      }
    },
    [authFetch, callback, enqueueErrorStack, getAllUploadedImages, t]
  )

  useEffect(() => {
    setIsLoading(true)
    if (!installation.installationStatementId) {
      const data = fromStatementModelToDtoTransformer(defaultValues)
      data.pvTrackers = []
      saveStatement(installation.deviceLinkId, installation.id, data).then(response => {
        if (!response) {
          return
        }

        getStatementFormData({
          ...installation,
          installationStatementId: response,
        }).finally(() => {
          setIsLoading(false)
        })
      })
    } else {
      getStatementFormData(installation).finally(() => {
        setIsLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const saveStatement = useCallback(
    async (deviceLinkId: number, installationId: number, statement: InstallationStatementApiDto): Promise<number | false> => {
      const response = await saveInstallationStatement(deviceLinkId, installationId, statement, authFetch)
      if (response.hasErrors) {
        return false
      }

      return response.value || false
    },
    [authFetch]
  )

  const confirmStatement = useCallback(
    async (
      deviceLinkId: number,
      installationId: number,
      statement: InstallationStatementApiDto
    ): Promise<ApiResult<InstallationStatementModel, Error | AuthFetchError>> => {
      return await confirmInstallationStatement(deviceLinkId, installationId, statement, authFetch)
    },
    [authFetch]
  )

  return { isLoading, saveStatement, confirmStatement }
}

export default useFetchData
